/**
 * @fileoverview Success/Error Overlay Components
 *
 * Modal overlays for session expired, login errors, and WebAuthn unsupported states.
 */

import React from 'react';
import {
  FiLock,
  FiX,
  FiShield,
  FiArrowRight
} from 'react-icons/fi';

/**
 * Session Expired Modal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onClose - Handle modal close
 * @returns {JSX.Element|null} Session expired modal or null
 */
export const SessionExpiredModal = ({ show, onClose }) => {
  if (!show) {
    return null;
  }

  // Check if dark mode is active
  const isDark = document.documentElement.classList.contains('dark');

  return (
    <div className="lp-modal-overlay active">
      <div className="lp-modal">
        <div className="lp-success-content">
          <div className="lp-success-icon" style={{ background: 'transparent', padding: 0 }}>
            <img
              src="/clockwise.png"
              alt="Clockwise"
              style={{
                width: 64,
                height: 64,
                filter: isDark ? 'brightness(0) invert(1)' : 'brightness(0)'
              }}
            />
          </div>
          <h2 className="lp-success-title">Session Expired</h2>
          <p className="lp-success-message">
            Your session has expired for security reasons. Please log in again to continue.
          </p>
          <button
            className="lp-btn lp-btn-primary"
            onClick={onClose}
            style={{ marginTop: '16px' }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Login Error Modal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Object} props.config - Error modal configuration
 * @param {string} props.config.title - Modal title
 * @param {string} props.config.message - Modal message
 * @param {string} props.config.type - Error type ('error', 'locked', 'deactivated')
 * @param {Function} props.onClose - Handle modal close
 * @returns {JSX.Element|null} Error modal or null
 */
export const LoginErrorModal = ({ show, config, onClose }) => {
  if (!show) {
    return null;
  }

  const getBackgroundGradient = () => {
    switch (config.type) {
      case 'locked':
        return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      case 'deactivated':
        return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
      default:
        return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    }
  };

  const getIcon = () => {
    switch (config.type) {
      case 'locked':
        return <FiLock size={40} />;
      case 'deactivated':
      default:
        return <FiX size={40} />;
    }
  };

  return (
    <div className="lp-modal-overlay active">
      <div className="lp-modal">
        <div className="lp-success-content">
          <div className="lp-success-icon" style={{ background: getBackgroundGradient() }}>
            {getIcon()}
          </div>
          <h2 className="lp-success-title">{config.title}</h2>
          <p className="lp-success-message">{config.message}</p>
          <button
            className="lp-btn lp-btn-primary"
            onClick={onClose}
            style={{ marginTop: '16px' }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * WebAuthn Unsupported Modal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onDismiss - Handle modal dismiss
 * @returns {JSX.Element|null} WebAuthn unsupported modal or null
 */
export const WebAuthnUnsupportedModal = ({ show, onDismiss }) => {
  if (!show) {
    return null;
  }

  return (
    <div className="lp-modal-overlay active">
      <div className="lp-modal">
        <div className="lp-modal-header" style={{ justifyContent: 'center' }}>
          <h2 className="lp-modal-title">Passkeys Not Supported</h2>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div className="lp-auth-choice-icon passkey" style={{ margin: '0 auto 16px', width: '64px', height: '64px' }}>
            <FiShield size={32} />
          </div>
        </div>
        <p className="lp-modal-description">
          Your browser or device doesn't support passkeys (WebAuthn).
          Passkeys provide a more secure and convenient way to sign in.
        </p>

        <div style={{ marginBottom: '24px', color: 'var(--lp-text-secondary)', fontSize: '14px' }}>
          <p style={{ marginBottom: '12px' }}>To use passkeys, try:</p>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
            <li>Updating your browser to the latest version</li>
            <li>Using a modern browser like Chrome, Safari, Firefox, or Edge</li>
            <li>Signing in from a device with biometric capabilities</li>
          </ul>
        </div>

        <button
          className="lp-btn lp-btn-primary"
          onClick={onDismiss}
        >
          <FiArrowRight style={{ marginRight: '8px' }} />
          Continue to Dashboard
        </button>
      </div>
    </div>
  );
};

/**
 * Reset Password Modal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Object} props.formik - Formik form instance
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onClose - Handle modal close
 * @returns {JSX.Element|null} Reset password modal or null
 */
export const ResetPasswordModal = ({ show, formik, loading, onClose }) => {
  const FiMail = require('react-icons/fi').FiMail;
  const FiArrowLeft = require('react-icons/fi').FiArrowLeft;

  if (!show) {
    return null;
  }

  return (
    <div className="lp-modal-overlay active">
      <div className="lp-modal">
        <div className="lp-modal-header">
          <button
            type="button"
            className="lp-modal-back"
            onClick={onClose}
            disabled={loading}
          >
            <FiArrowLeft size={18} />
          </button>
          <h2 className="lp-modal-title">Reset Password</h2>
        </div>
        <p className="lp-modal-description">
          Enter your email address and we'll send you instructions to reset your password.
        </p>

        <form onSubmit={formik.handleSubmit}>
          <div className="lp-form-group" style={{ marginBottom: '24px' }}>
            <label className="lp-form-label">Email Address</label>
            <div className="lp-input-wrapper">
              <FiMail className="lp-input-icon" />
              <input
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                className={`lp-form-input ${formik.touched.email && formik.errors.email ? 'lp-input-error' : ''}`}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading}
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <div className="lp-error-message">{formik.errors.email}</div>
            )}
          </div>

          <button
            type="submit"
            className="lp-btn lp-btn-primary"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
      </div>
    </div>
  );
};
